/**
 * Messaging's delivery option: the grace period before a missed-message email.
 *
 * Registered on the notification block's extension slot
 * (`shared/notifications/deliveryOptions/index.ts`), so it renders inside the
 * block in BOTH mounts — the messaging settings page and the central
 * /settings?tab=notifications tab. It must never be rendered beside the block.
 *
 * This shapes HOW an opted-in email is delivered; WHETHER to email at all is the
 * catalog's `email` channel on the messaging events, one level up in the block.
 *
 * Deliberately imports no settings PAGE — the delivery-option barrel is loaded
 * eagerly by every notification mount.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { apiGet, apiPut } from '../../../shared/api/axiosInstance';
import { InlineEditableSelect } from '../../design-system/components/FormPatterns';

interface MessagingUserSettings {
  email_delay_minutes: number;
}

const DELAY_MINUTES = [0, 1, 2, 5, 10, 15, 30, 60] as const;

export function MessagingEmailDelay() {
  const { t } = useTranslation('messaging');
  const queryClient = useQueryClient();

  const { data: settings } = useQuery({
    queryKey: ['messaging', 'settings', 'user'],
    queryFn: async () => apiGet<MessagingUserSettings>('/api/v1/messaging/settings/user'),
  });

  const updateMutation = useMutation({
    mutationFn: async (email_delay_minutes: number) =>
      apiPut<MessagingUserSettings>('/api/v1/messaging/settings/user', { email_delay_minutes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messaging', 'settings', 'user'] });
    },
  });

  const options = DELAY_MINUTES.map((minutes) => ({
    value: String(minutes),
    label: t(`settings.emailDelayOptions.${minutes}`),
  }));

  return (
    <div className="max-w-md space-y-1">
      <InlineEditableSelect
        label={t('settings.emailDelay')}
        value={String(settings?.email_delay_minutes ?? 5)}
        onChange={(value: string) => updateMutation.mutate(Number(value))}
        options={options}
      />
      <p className="text-xs text-muted-foreground">{t('settings.emailDelayHelp')}</p>
    </div>
  );
}
